import { z } from 'zod'

import type { HangmanView } from './games/hangman.js'
import type { MathsRaceView } from './games/maths-race.js'
import type { MemoryView } from './games/memory.js'
import { RACE_LEVELS } from './games/maths-race.js'
import { TEDDY_KEYS } from './teddies.js'
import { AVATAR_KEYS } from './avatars.js'

/**
 * What the server sends a client, as schemas rather than as a promise.
 *
 * The client used to assert these types and hope. That is not a check, and it
 * cost us: when the room payload changed shape, nothing failed to compile, the
 * server's tests passed because the server was right, and the web's tests
 * passed because its fixtures were built to its own wrong idea of the contract.
 * The whole suite was green and the seam between them was broken.
 *
 * Two passing tests either side of a seam do not test the seam. Parsing at the
 * boundary is what turns that into a loud failure instead of a quiet one.
 */

const teddyKey = z.enum(TEDDY_KEYS)

// A seat's avatar can be Daddy, who is not a teddy. Memory's card faces above
// stay teddies only — he is not one of the pairs.
const avatarKey = z.enum(AVATAR_KEYS)

export const hangmanViewSchema = z
  .object({
    letters: z.array(z.string().nullable()),
    hint: z.string(),
    guessed: z.array(z.string()),
    livesRemaining: z.number(),
    status: z.enum(['playing', 'won', 'lost']),
    currentPlayerId: z.string().nullable(),
    answer: z.string().nullable(),
    lastGuess: z
      .object({ playerId: z.string(), letter: z.string(), correct: z.boolean() })
      .nullable(),
    roundPlayerIds: z.array(z.string()),
  })
  .strict()

export const mathsRaceViewSchema = z
  .object({
    status: z.enum(['playing', 'finished']),
    roundLength: z.number(),
    level: z.enum(RACE_LEVELS),
    players: z.array(
      z.object({
        playerId: z.string(),
        displayName: z.string(),
        prompt: z.string().nullable(),
        asked: z.number(),
      }),
    ),
    you: z
      .object({
        playerId: z.string(),
        prompt: z.string().nullable(),
        correct: z.number(),
        finished: z.boolean(),
        lastAnswer: z
          .object({
            prompt: z.string(),
            expected: z.number(),
            given: z.number(),
            correct: z.boolean(),
            // Whether that answer left the question open for another go, so a
            // phone in a room says "try again" exactly when a phone on its own
            // would. The rule lives in the engine; this carries its answer.
            moreGoes: z.boolean(),
          })
          .nullable(),
      })
      .nullable(),
    together: z.object({ correct: z.number(), outOf: z.number() }).strict().nullable(),
  })
  .strict()

export const memoryViewSchema = z
  .object({
    cards: z.array(
      z.object({ card: z.number(), teddy: teddyKey.nullable(), matched: z.boolean() }),
    ),
    status: z.enum(['playing', 'finished']),
    currentPlayerId: z.string().nullable(),
    flips: z.number(),
    pairsFound: z.number(),
    pairsTotal: z.number(),
    playerPairs: z.record(z.string(), z.number()),
    revealUntil: z.number().nullable(),
    roundPlayerIds: z.array(z.string()),
  })
  .strict()

export const roomRoundViewSchema = z.discriminatedUnion('key', [
  z.object({ key: z.literal('hangman'), view: hangmanViewSchema }),
  z.object({ key: z.literal('maths'), view: mathsRaceViewSchema }),
  z.object({ key: z.literal('memory'), view: memoryViewSchema }),
])

export const roomPlayerSchema = z
  .object({
    id: z.string(),
    displayName: z.string(),
    avatarKey: avatarKey.nullable(),
    connected: z.boolean(),
  })
  .strict()

export const roomStateSchema = z
  .object({
    code: z.string(),
    hostConnected: z.boolean(),
    turnEndsAt: z.number().nullable(),
    /** Which seat this payload was built for; null for a television. */
    you: z.string().nullable(),
    players: z.array(roomPlayerSchema),
    game: roomRoundViewSchema.nullable(),
  })
  .strict()

export const roomCreatedSchema = z.object({ code: z.string(), hostToken: z.string() }).strict()

/**
 * "You have played ten games."
 *
 * Deliberately carries nothing but the number. There is no rank, no title and
 * nothing to collect, so there is nothing else to send.
 */
export const roomMilestoneSchema = z.object({ milestone: z.number().int().min(1) }).strict()

export type RoomMilestonePayload = z.infer<typeof roomMilestoneSchema>

export const roomJoinedSchema = z
  .object({
    seatId: z.string(),
    token: z.string(),
    rejoined: z.boolean(),
  })
  .strict()

export const roomErrorSchema = z.object({ reason: z.string() }).strict()

export const roomClosedSchema = z.object({ code: z.string() }).strict()

/**
 * Readonly, all the way down.
 *
 * A message that has arrived is a record of what somebody else said. Nothing on
 * this side has any business changing it, and the views these mirror are
 * readonly for the same reason.
 */
export type HangmanViewPayload = Immutable<z.infer<typeof hangmanViewSchema>>
export type MathsRaceViewPayload = Immutable<z.infer<typeof mathsRaceViewSchema>>
export type MemoryViewPayload = Immutable<z.infer<typeof memoryViewSchema>>
export type RoomRoundViewPayload = Immutable<z.infer<typeof roomRoundViewSchema>>
export type RoomStatePayload = Immutable<z.infer<typeof roomStateSchema>>
export type RoomPlayerPayload = Immutable<z.infer<typeof roomPlayerSchema>>
export type RoomCreatedPayload = z.infer<typeof roomCreatedSchema>
export type RoomJoinedPayload = z.infer<typeof roomJoinedSchema>
export type RoomErrorPayload = z.infer<typeof roomErrorSchema>
export type RoomClosedPayload = z.infer<typeof roomClosedSchema>

/**
 * Ties each schema to the view it mirrors, at compile time.
 *
 * A schema maintained separately from the type it describes is the same bug
 * with more steps: it drifts, silently, and the first thing to notice is a
 * client refusing perfectly good payloads. These fail the build instead.
 *
 * Checked twice, because once is not enough. `extends` treats a missing
 * *optional* property as compatible in both directions, so adding `foo?: number`
 * to a view and forgetting the schema slips straight past a plain comparison —
 * and since the schemas are strict, the payload would then be refused outright
 * at run time. Comparing again with every property required is what sees it.
 */
type Exact<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never

type Mirrors<Schema, View> =
  Exact<Schema, Mutable<View>> extends true
    ? Exact<Required<Schema>, Required<Mutable<View>>>
    : never

const _hangmanMatches: Mirrors<z.infer<typeof hangmanViewSchema>, HangmanView> = true
const _mathsMatches: Mirrors<z.infer<typeof mathsRaceViewSchema>, MathsRaceView> = true
const _memoryMatches: Mirrors<z.infer<typeof memoryViewSchema>, MemoryView> = true

void _hangmanMatches
void _mathsMatches
void _memoryMatches

/**
 * The views are readonly all the way down; what Zod infers is not.
 *
 * Comparing them without this would fail on the modifier rather than on the
 * shape, which is the thing actually worth checking.
 */
type Mutable<T> = T extends readonly (infer Item)[]
  ? Mutable<Item>[]
  : T extends object
    ? { -readonly [Key in keyof T]: Mutable<T[Key]> }
    : T

type Immutable<T> = T extends readonly (infer Item)[]
  ? readonly Immutable<Item>[]
  : T extends object
    ? { readonly [Key in keyof T]: Immutable<T[Key]> }
    : T
